import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import type { CampaignResultStatus } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { campaignSelectMenuProps } from "./campaigns-presentation.js";
import {
  CAMPAIGN_RESULT_FILTER_OPTIONS,
  campaignResultsHaveActiveFilters,
} from "./campaign-results-presentation.js";

export function CampaignResultsToolbar({
  search,
  resultStatus,
  filteredTotal,
  onSearchChange,
  onResultStatusChange,
  onClearFilters,
}: {
  search: string;
  resultStatus: CampaignResultStatus | "ALL";
  filteredTotal: number | null;
  onSearchChange: (value: string) => void;
  onResultStatusChange: (status: CampaignResultStatus | "ALL") => void;
  onClearFilters: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const filtersActive = campaignResultsHaveActiveFilters({ search, resultStatus });

  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
      <label className="relative block min-w-0 flex-1 basis-full md:basis-0 md:min-w-[12rem] md:max-w-[22rem]">
        <span className="sr-only">{t("campaigns.results.search")}</span>
        <Search
          size={14}
          className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("campaigns.results.searchPlaceholder")}
          className="h-8 w-full rounded-md border border-line/70 bg-surface ps-8 pe-2.5 text-[0.8125rem] text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50"
          data-testid="campaign-results-search"
        />
      </label>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="w-full min-w-0 shrink-0 sm:w-44" data-testid="campaign-results-filter">
          <SimpleSelect
            {...campaignSelectMenuProps}
            value={resultStatus}
            onValueChange={(value) =>
              onResultStatusChange(value as CampaignResultStatus | "ALL")
            }
            ariaLabel={t("campaigns.results.filter")}
            triggerClassName="h-8 w-full min-w-0 text-start text-[0.75rem]"
            options={CAMPAIGN_RESULT_FILTER_OPTIONS.map((option) => ({
              value: option,
              label:
                option === "ALL"
                  ? t("campaigns.results.filters.all")
                  : t(`campaigns.results.status.${option}`),
            }))}
          />
          <span className="sr-only">{resultStatus}</span>
        </div>
        {filteredTotal != null ? (
          <p
            className="m-0 flex h-8 shrink-0 items-center whitespace-nowrap text-[0.75rem] text-muted"
            data-testid="campaign-results-filtered-count"
          >
            {t("campaigns.results.filteredCount", {
              count: filteredTotal,
              formatted: new Intl.NumberFormat(i18n.language).format(filteredTotal),
            })}
          </p>
        ) : null}
        {filtersActive ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 shrink-0 text-[0.75rem]"
            onClick={onClearFilters}
            data-testid="campaign-results-clear-filters"
          >
            <X size={14} aria-hidden="true" />
            {t("campaigns.results.clearFilters")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
